import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, User, Mail, Phone, Clock, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { token } = useAuth();

  useEffect(() => { fetchMessages(); }, [token]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch { toast.error('Erreur lors du chargement des messages'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/contact/${messageId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message supprime');
      setConfirmDelete(null);
      fetchMessages();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const filteredMessages = messages.filter(msg => filter === 'all' || msg.status === filter);

  const getSubjectLabel = (subject) => {
    const labels = { devis: 'Demande de devis', info: 'Informations', reservation: 'Reservation', partenariat: 'Partenariat', autre: 'Autre' };
    return labels[subject] || subject;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout title="Messages">
      <div className="space-y-6" data-testid="admin-messages">
        {/* Stats */}
        <div className="flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">Total: {messages.length}</div>
          <div className="px-3 py-1.5 rounded-full bg-[#D4AF37]/20 text-sm font-medium text-[#D4AF37]">Non lus: {messages.filter(m => m.status === 'new').length}</div>
          <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">Repondus: {messages.filter(m => m.status === 'replied').length}</div>
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between">
          <p className="text-[#4A5568]">{filteredMessages.length} message{filteredMessages.length > 1 ? 's' : ''}</p>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="new">Non lus</SelectItem>
              <SelectItem value="read">Lus</SelectItem>
              <SelectItem value="replied">Repondu</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}</div>
        ) : filteredMessages.length > 0 ? (
          <div className="space-y-4">
            {filteredMessages.map(message => (
              <Card key={message.id} className={message.status === 'new' ? 'border-l-4 border-l-[#D4AF37]' : ''} data-testid={`message-card-${message.id}`}>
                <CardContent className="p-3 sm:p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                    <div className="space-y-2 flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0A2540] rounded-lg flex items-center justify-center shrink-0">
                          <MessageSquare className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-[#0A2540]">{getSubjectLabel(message.subject)}</h3>
                          <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                            <Clock className="h-3 w-3" />{formatDate(message.created_at)}
                          </div>
                        </div>
                        {message.status === 'new' && <Badge className="bg-[#D4AF37] text-white">Nouveau</Badge>}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-[#4A5568]"><User className="h-3.5 w-3.5" />{message.name}</div>
                        <div className="flex items-center gap-2 text-[#4A5568]"><Mail className="h-3.5 w-3.5" />{message.email}</div>
                        {message.phone && <div className="flex items-center gap-2 text-[#4A5568]"><Phone className="h-3.5 w-3.5" />{message.phone}</div>}
                      </div>

                      <p className="text-[#4A5568] bg-[#F8F9FA] p-3 rounded-lg whitespace-pre-wrap text-sm">{message.message}</p>
                    </div>

                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="sm" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white h-8 text-xs" asChild>
                        <a href={`mailto:${message.email}?subject=Re: ${getSubjectLabel(message.subject)}`}>
                          <Mail className="h-3.5 w-3.5 mr-1" />Repondre
                        </a>
                      </Button>
                      {message.phone && (
                        <Button size="sm" variant="outline" className="h-8 text-xs" asChild>
                          <a href={`tel:${message.phone}`}><Phone className="h-3.5 w-3.5 mr-1" />Appeler</a>
                        </Button>
                      )}
                      {confirmDelete === message.id ? (
                        <div className="flex items-center gap-1">
                          <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(message.id)} data-testid={`confirm-delete-message-${message.id}`}>
                            Supprimer
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmDelete(null)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:bg-red-50 justify-start" onClick={() => setConfirmDelete(message.id)} data-testid={`delete-message-${message.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" />Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#4A5568]">Aucun message pour le moment</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
